import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, Plus } from 'lucide-react';
import { usePageTransition, TextReveal } from '../PageTransition';
import { NepaleseFrame } from './WoodenFrames';

const MenuSection = () => {
  const { t } = useTranslation();
  const { navigateWithTransition } = usePageTransition();

  const menuItems = [
    { name: 'Butter Chicken', desc: 'Tender chicken in rich tomato cream', image: 'https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?q=80&w=2070', price: '18,50€' },
    { name: 'Chicken Tikka Masala', desc: 'Yogurt marinated grilled chicken in tikka masala', image: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?q=80&w=2070', price: '18,50€' },
    { name: 'Paneer Tikka', desc: 'Charred cottage cheese, mint chutney', image: 'https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?q=80&w=2070', price: '17,00€' },
  ];

  const handleAddToCart = (item) => {
    try {
      const existing = JSON.parse(localStorage.getItem('sunsari_pending_cart') || '[]');
      existing.push({ name: item.name, description: item.desc, price: item.price, image: item.image });
      localStorage.setItem('sunsari_pending_cart', JSON.stringify(existing));
    } catch {}
    navigateWithTransition('/order');
  };

  return (
    <section className="relative py-24 md:py-32 bg-[#1A1A1A]">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <TextReveal><p className="text-[#C97A2B] text-xs tracking-[0.3em] uppercase mb-4">{t('menu.subtitle')}</p></TextReveal>
          <TextReveal delay={0.1}>
            <h2 className="text-4xl md:text-5xl lg:text-6xl text-white" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
              {t('menu.title').split(' ').map((word, i) => word === 'Menu' || word === 'Menyn' ? <span key={i}><span className="italic text-[#C97A2B]">{word}</span></span> : <span key={i}>{word} </span>)}
            </h2>
          </TextReveal>
        </div>

        <div className="grid md:grid-cols-3 gap-8 md:gap-10 mb-16">
          {menuItems.map((item, index) => (
            <motion.div key={item.name} className="group flex flex-col" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15, duration: 0.8 }} data-cursor-hover>
              <NepaleseFrame src={item.image} alt={item.name} className="mb-4" />
              <div className="flex justify-between items-start px-2 mb-4">
                <div className="flex-1 pr-3">
                  <h3 className="text-white text-xl md:text-2xl mb-2 group-hover:text-[#C97A2B] transition-colors duration-300" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>{item.name}</h3>
                  <p className="text-white/50 text-sm font-light">{item.desc}</p>
                </div>
                <p className="text-[#C97A2B] text-xl font-serif whitespace-nowrap">{item.price}</p>
              </div>
              <div className="px-2 mt-auto">
                <button
                  onClick={() => handleAddToCart(item)}
                  className="w-full flex items-center justify-center gap-2 bg-[#C97A2B] hover:bg-[#B65A3A] text-white py-3 rounded-full text-xs tracking-[0.15em] uppercase font-semibold transition-colors"
                  data-cursor-hover
                  data-testid={`add-to-cart-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Plus className="w-3.5 h-3.5" /> Add to Cart
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center flex flex-col sm:flex-row gap-4 justify-center items-center">
          <motion.button onClick={() => navigateWithTransition('/menu')}
            className="border border-[#C97A2B] text-[#C97A2B] px-10 py-5 text-xs tracking-[0.15em] uppercase hover:bg-[#C97A2B] hover:text-black transition-all duration-500"
            whileHover={{ scale: 1.02 }} data-cursor-hover>{t('menu.viewFullMenu')}</motion.button>
          <motion.button onClick={() => navigateWithTransition('/order')}
            className="inline-flex items-center gap-2 bg-[#C97A2B] text-white px-10 py-5 text-xs tracking-[0.15em] uppercase font-semibold hover:bg-[#B65A3A] transition-all duration-500"
            whileHover={{ scale: 1.02 }} data-cursor-hover>
            <ShoppingBag className="w-4 h-4" /> Order Online
          </motion.button>
        </div>
      </div>
    </section>
  );
};

export default MenuSection;
